import { FC, Fragment, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Checkbox, Dialog, DialogPanel, Transition } from "@headlessui/react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import toast from "react-hot-toast";
import PrivacyPolicy from "containers/PageHome/PrivacyPolicy";
import TermsandConditions from "containers/PageHome/RentalAgreement";
import Cancellation from "containers/PageHome/Cancellation";
import BackRouteButton from "shared/BackRouteButton/BackRouteButton";
import { useData } from "data/data-provider";
import { resolvePolicyCheckboxCopy } from "utils/policyCheckboxCopy";
import { sanitizeHtml } from "utils/sanitizeHtml";

interface PayLaterPoliciesState {
  bookingIdentifier: string;
  pdfUrl: string;
  agreementContent: any;
  privacyPolicyContent: any;
  cancellationPolicyContent: any;
}

const PayLaterPoliciesPage: FC = () => {
  const { companyDetails } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as PayLaterPoliciesState;
  const {
    bookingIdentifier,
    pdfUrl,
    agreementContent,
    privacyPolicyContent,
    cancellationPolicyContent,
  } = state;

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rentalAgreementAccepted, setRentalAgreementAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [isTermOpen, setIsTermOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [isRentalAgreementPdfOpen, setIsRentalAgreementPdfOpen] = useState(false);
  const policyCheckboxCopy = useMemo(
    () => resolvePolicyCheckboxCopy(companyDetails),
    [companyDetails],
  );

  const handleProceed = () => {
    if (!rentalAgreementAccepted) {
      toast.error("Please review and accept the Rental Agreement");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please review and accept the Terms and Conditions");
      return;
    }
    if (!privacyPolicyAccepted) {
      toast.error("Please review and accept the Privacy & Cancellation Policies");
      return;
    }
    toast.success("Your Booking is reserved");
    navigate(`/booking-details?id=${bookingIdentifier}`);
  };

  const allAccepted = rentalAgreementAccepted && termsAccepted && privacyPolicyAccepted;

  const toggleAcceptAll = (checked: boolean) => {
    setRentalAgreementAccepted(checked);
    setTermsAccepted(checked);
    setPrivacyPolicyAccepted(checked);
    if (checked) toast.success("All policies accepted");
  };

  return (
    <>
      <div className="container mt-11 pb-10">
        <div className="max-w-2xl mx-auto">
          <div className="mt-2 mb-6">
            <BackRouteButton />
          </div>

          <div className="w-full flex flex-col sm:rounded-md sm:border border-neutral-200 space-y-4 px-0 sm:p-6 xl:p-8">
            <h2 className="text-2xl font-bold text-text-color">
              Review &amp; Accept Policies
            </h2>
            <p className="text-sm text-text-color">
              Please review and accept all policies before proceeding to your booking.
            </p>

            {/* Rental Agreement */}
            <div className="flex gap-2 p-4 border border-neutral-200 rounded-md overflow-hidden">
              <Checkbox
                checked={rentalAgreementAccepted}
                onChange={(checked) => setRentalAgreementAccepted(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset mr-4 flex-shrink-0"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-200 ${rentalAgreementAccepted ? "visible" : "invisible"}`}
                />
              </Checkbox>
              <div>
                <div
                  className="policy-checkbox-copy text-text-color text-lg font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(policyCheckboxCopy.rentalAgreementText),
                  }}
                />
                <div
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsRentalAgreementPdfOpen(true)}
                >
                  Rental Agreement
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="flex gap-2 p-4 border border-neutral-200 rounded-md overflow-hidden">
              <Checkbox
                checked={termsAccepted}
                onChange={(checked) => setTermsAccepted(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset mr-4 flex-shrink-0"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-200 ${termsAccepted ? "visible" : "invisible"}`}
                />
              </Checkbox>
              <div>
                <div
                  className="policy-checkbox-copy text-text-color text-lg font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(policyCheckboxCopy.termsConditionsText),
                  }}
                />
                <div
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsTermOpen(true)}
                >
                  Terms and Conditions
                </div>
              </div>
            </div>

            {/* Privacy Policy & Cancellation Policy */}
            <div className="flex gap-2 p-4 border border-neutral-200 rounded-md overflow-hidden">
              <Checkbox
                checked={privacyPolicyAccepted}
                onChange={(checked) => setPrivacyPolicyAccepted(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset mr-4 flex-shrink-0"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-200 ${privacyPolicyAccepted ? "visible" : "invisible"}`}
                />
              </Checkbox>
              <div>
                <div
                  className="policy-checkbox-copy text-text-color text-lg font-medium"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(policyCheckboxCopy.privacyCancellationText),
                  }}
                />
                <span
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsPrivacyOpen(true)}
                >
                  Privacy Policy
                </span>
                <span className="text-text-color text-xl font-extrabold mx-2">
                  &amp;
                </span>
                <span
                  className="text-text-color text-xl font-extrabold cursor-pointer underline"
                  onClick={() => setIsCancellationOpen(true)}
                >
                  Cancellation Policy
                </span>
              </div>
            </div>

            <div className="flex gap-2 mt-6 p-4 border border-neutral-200 rounded-md overflow-hidden items-center">
              <Checkbox
                checked={allAccepted}
                onChange={(checked) => toggleAcceptAll(checked)}
                className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset mr-2"
              >
                <CheckIcon className={`size-5 stroke-primary-200 ${allAccepted ? "visible" : "invisible"}`} />
              </Checkbox>
              <div className="text-text-color "><p className="font-bold">Accept all policies</p>  <p className="text-text-color text-sm">
                By proceeding you agree to the{' '}
                <button type="button" onClick={() => setIsRentalAgreementPdfOpen(true)} className="underline text-text-color">
                  Rental Agreement
                </button>
                ,{' '}
                <button type="button" onClick={() => setIsTermOpen(true)} className="underline text-text-color">
                  Terms and Conditions
                </button>
                ,{' '}
                <button type="button" onClick={() => setIsPrivacyOpen(true)} className="underline text-text-color">
                  Privacy Policies
                </button>
                , and the{' '}
                <button type="button" onClick={() => setIsCancellationOpen(true)} className="underline text-text-color">
                  Cancellation Policies
                </button>
                .
              </p></div>
            </div>

            <ButtonPrimary className="w-full rounded-md" onClick={handleProceed}>
              Proceed to Booking
            </ButtonPrimary>
          </div>
        </div>
      </div>

      {/* Terms & Conditions sub-modal */}
      <Transition appear show={isTermOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsTermOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isTermOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsTermOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                        <TermsandConditions data={agreementContent} />
                      </div>
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-neutral-200 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setTermsAccepted(true);
                        setIsTermOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Privacy Policy sub-modal */}
      <Transition appear show={isPrivacyOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsPrivacyOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isPrivacyOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsPrivacyOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                        <PrivacyPolicy terms={privacyPolicyContent} />
                      </div>
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-neutral-200 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setPrivacyPolicyAccepted(true);
                        setIsPrivacyOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Cancellation Policy sub-modal */}
      <Transition appear show={isCancellationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsCancellationOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isCancellationOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4 z-0">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsCancellationOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                        <Cancellation terms={cancellationPolicyContent} />
                      </div>
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-neutral-200 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setPrivacyPolicyAccepted(true);
                        setIsCancellationOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Rental Agreement PDF sub-modal */}
      <Transition appear show={isRentalAgreementPdfOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsRentalAgreementPdfOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                show={isRentalAgreementPdfOpen}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <div className="absolute left-4 top-4">
                    <button className="focus:outline-none focus:ring-0">
                      <XMarkIcon
                        onClick={() => setIsRentalAgreementPdfOpen(false)}
                        className="w-5 h-5 text-text-color"
                      />
                    </button>
                  </div>
                  <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                    <div className="flex-1 flex flex-col bg-bg-color z-10">
                      <iframe
                        src={pdfUrl}
                        title="Rental Agreement"
                        className="w-full h-full"
                        style={{ minHeight: "60vh" }}
                      />
                    </div>
                  </div>
                  <div className="justify-end px-4 py-3 bg-bg-color border-t border-neutral-200 flex">
                    <ButtonPrimary
                      sizeClass="px-6 py-3 !rounded-md"
                      onClick={() => {
                        setRentalAgreementAccepted(true);
                        setIsRentalAgreementPdfOpen(false);
                      }}
                    >
                      Accept
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default PayLaterPoliciesPage;
